import React, { type ReactElement, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { CgSpinner } from "react-icons/cg";
import { FaEnvelope, FaUser } from "react-icons/fa";
import { FaClipboardUser } from "react-icons/fa6";
import { toast } from "react-toastify";
import { OutboundLink } from "gatsby-plugin-google-gtag";

import InputSwitch from "@components/input_switch";
import Modal from "@components/modal";
import { useAccountDetailState } from "@stores/account_detail";
import { useAuthenticationState } from "@stores/authentication";
import axios from "@utilities/axios";
import { defaultRipple, isDebugMode } from "@utilities/helper";
import { getCorrectedEmail, toastEmailCorrected } from "@utilities/onboarding";
import { AnimatePresence, motion } from "motion/react";

interface UserInformationForm {
  full_name: string;
  email: string;
}
const FillInformationPopup = (): ReactElement => {
  const { t } = useTranslation();
  const accountDetailState = useAccountDetailState();
  const authenticationState = useAuthenticationState();

  const { register, handleSubmit } = useForm<UserInformationForm>();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isCheckedBNESIMMarketing, setIsCheckedBNESIMMarketing] =
    useState<boolean>(true);

  const supportEmail = "support@bnesim.com";
  const updateInformation: SubmitHandler<UserInformationForm> = async data => {
    setLoading(true);

    const [userUpdatingStatus, bnesimMarketingStatus] = await Promise.all([
      updateUserInformation(data.email, data.full_name),
      isCheckedBNESIMMarketing ? updateBNESIMMarketingConsent() : true,
    ]);

    if (userUpdatingStatus && bnesimMarketingStatus) {
      accountDetailState.setNeedToFillInformation(false);
    }
    setLoading(false);
  };

  const updateUserInformation = async (
    email: string,
    full_name: string,
  ): Promise<boolean> => {
    const userFormData = new FormData();
    userFormData.append("action", "edit");
    userFormData.append("email", email);
    userFormData.append("full_name", full_name);

    const url = "/my_account_details/";
    try {
      const response = await axios.post(url, userFormData);
      if (isDebugMode()) {
        console.log("User JSON response:", response.data);
      }
      const correctedEmail = getCorrectedEmail(response.data);
      if (correctedEmail != null) {
        toastEmailCorrected(t, correctedEmail);
      }
      return true;
    } catch (error: any) {
      toast.error(error.message, {
        autoClose: 5000,
      });
      if (error.response) {
        console.error(
          `[fill-information-popup] Response status: ${error.response.status}`,
        );
      } else {
        console.error("[fill-information-popup]", error.message);
      }
      return false;
    }
  };

  const updateBNESIMMarketingConsent = async (): Promise<boolean> => {
    const emailFormData = new FormData();
    emailFormData.append("action", "add");
    emailFormData.append("service", "1"); // email

    const notificationFormData = new FormData();
    notificationFormData.append("action", "add");
    notificationFormData.append("service", "2"); // notification

    const url = "/subscriptions/";

    try {
      const [emailResponseData, notificationResponseData] = await Promise.all([
        axios.post(url, emailFormData),
        axios.post(url, notificationFormData),
      ]);

      if (isDebugMode()) {
        console.log("Email JSON response:", emailResponseData.data);
        console.log(
          "Notification JSON response:",
          notificationResponseData.data,
        );
      }
      return true;
    } catch (error: any) {
      toast.error(error.message, {
        autoClose: 5000,
      });
      if (error.response) {
        console.error(
          `[fill-information-popup] Response status: ${error.response.status}`,
        );
      } else {
        console.error("[fill-information-popup]", error.message);
      }
      return false;
    }
  };

  return (
    <Modal
      isModalOpen={
        authenticationState.isAuthenticated() &&
        accountDetailState.isNeedToFillInformation
      }
      setIsModalOpen={() => {}}
      isCloseButtonExist={false}
      closeOnBackgroundClick={false}
      badge={
        <span className="absolute left-1/2 top-0 z-30 size-20 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white p-2">
          <div className="aspect-square size-full rounded-full bg-primary p-3">
            <FaClipboardUser className="size-full text-white" />
          </div>
        </span>
      }
    >
      <div className="mt-4">
        <form
          className="flex flex-col gap-4"
          onSubmit={handleSubmit(updateInformation)}
        >
          <label className="relative w-full">
            <FaUser className="absolute left-[11px] right-auto top-[11px] z-[1] size-[20px] text-primary rtl:left-auto rtl:right-[11px]" />
            <input
              {...register("full_name", {
                required: false,
              })}
              name="full_name"
              placeholder={t("full_name") ?? ""}
              autoComplete="on"
              autoCorrect="on"
              type="text"
              className="custom-form-control block w-full !ps-10"
              defaultValue={accountDetailState.license.name}
            />
          </label>
          <label className="relative w-full">
            <FaEnvelope className="absolute left-[11px] right-auto top-[11px] z-[1] size-[20px] text-primary rtl:left-auto rtl:right-[11px]" />
            <input
              {...register("email", {
                required: true,
              })}
              name="email"
              placeholder={t("email") ?? ""}
              autoComplete="on"
              autoCorrect="on"
              type="email"
              className="custom-form-control block w-full !ps-10"
              defaultValue={accountDetailState.license.email}
            />
          </label>
          <div className="flex items-center gap-3">
            <InputSwitch
              value={isCheckedBNESIMMarketing}
              handleCheck={() => {
                setIsCheckedBNESIMMarketing(prev => !prev);
              }}
            />
            <span className="max-w-[calc(100%-3.5rem)]">
              <Trans
                i18nKey="marketing_consent_for_bnesim"
                values={{
                  email: supportEmail,
                }}
                components={{
                  "privacy-policy-link": (
                    <OutboundLink
                      href={process.env.GATSBY_PRIVACY_POLICY_LINK}
                      target="_blank"
                      rel="noreferrer"
                      className="custom-link-primary"
                    />
                  ),
                  a: (
                    <a
                      href={"mailto:" + supportEmail}
                      className="custom-link-primary"
                    />
                  ),
                }}
              />
            </span>
          </div>
          <button
            ref={defaultRipple}
            className="custom-btn-primary flex w-full items-center justify-center gap-2"
            type="submit"
            disabled={isLoading}
          >
            <AnimatePresence>
              {isLoading && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <CgSpinner className="animate-spin text-white" />
                </motion.span>
              )}
            </AnimatePresence>
            <span>{t("continue")}</span>
          </button>
        </form>
      </div>
    </Modal>
  );
};

export default FillInformationPopup;
